import { motion } from 'framer-motion'
import 'primeicons/primeicons.css'
import { useTheme } from '../contexts/ThemeContext'

export default function Footer() {
    const { theme } = useTheme()
    
    const handleZaloContact = (phone: string) => {
        window.open(`https://zalo.me/${phone}`, '_blank')
    }

    return (
        <footer className={`relative ${theme === 'dark' ? 'bg-black text-white' : 'bg-gray-200 text-gray-900'}`}>
            <div className="container mx-auto px-4 py-10">
                <div className="grid md:grid-cols-3 gap-8 items-start">
                    {/* About/Logo */}
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4 }}
                    >
                        <div className="flex items-center gap-3 mb-3">
                            <img
                                src="/images/logo.png"
                                alt="Sài Gòn FC Logo"
                                width={50}
                                height={50}
                                className="object-contain"
                            />
                            <div className="flex flex-col leading-tight">
                                <span className="font-bold text-gradient text-lg">
                                    SAIGON FC
                                </span>
                                <span className={`font-semibold text-xs ${theme === 'dark' ? 'text-gold-400' : 'text-gray-800'}`}>
                                    Training League
                                </span>
                            </div>
                        </div>

                        <p className={`text-sm leading-relaxed max-w-md ${theme === 'dark' ? 'text-gray-400' : 'text-gray-700'}`}>
                            Xây nền tảng vững chắc - Kỷ luật mỗi ngày để vươn xa
                        </p>
                    </motion.div>

                    {/* Contact */}
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: 0.05 }}
                    >
                        <h3 className={`text-xl font-bold mb-4 ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}>
                            Liên hệ
                        </h3>
                        <div className={`space-y-3 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                            <div className="flex items-center gap-2">
                                <i className={`pi pi-phone text-gold-400`}></i>
                                <button
                                    onClick={() => handleZaloContact('0976950338')}
                                    className={`hover:underline ${theme === 'dark' ? 'text-white hover:text-gold-400' : 'text-gray-900 hover:text-gold-500'} transition`}
                                >
                                    0976 950 338 (Coach Khang)
                                </button>
                            </div>
                            <div className="flex items-center gap-2">
                                <i className={`pi pi-phone text-gold-400`}></i>
                                <button
                                    onClick={() => handleZaloContact('0703577789')}
                                    className={`hover:underline ${theme === 'dark' ? 'text-white hover:text-gold-400' : 'text-gray-900 hover:text-gold-500'} transition`}
                                >
                                    0703 577 789 (Coach Nhật)
                                </button>
                            </div>
                            <div className="flex items-center gap-2">
                                <i className={`pi pi-envelope text-gold-400`}></i>
                                <a
                                    href="mailto:coachkhang.fromsaigonfc@gmail.com"
                                    className={`hover:underline ${theme === 'dark' ? 'text-white hover:text-gold-400' : 'text-gray-900 hover:text-gold-500'} transition`}
                                >
                                    coachkhang.fromsaigonfc@gmail.com
                                </a>
                            </div>
                        </div>
                    </motion.div>

                    {/* Class Locations */}
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: 0.05 }}
                    >
                        <h3 className={`text-xl font-bold mb-4 ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}>
                            Địa điểm lớp học
                        </h3>
                        <div className={`space-y-4 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                            <a href="https://maps.app.goo.gl/UjZR6ZcAs1DwmPMDA" target="_blank" rel="noopener noreferrer" className="block group">
                                <div className="flex items-center gap-2 mb-1">
                                    <i className={`pi pi-map-marker text-gold-400`}></i>
                                    <span className={`font-semibold group-hover:text-gold-400 transition ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                        Sân Bóng Vạn Xuân (Tân Bình)
                                    </span>
                                </div>
                                <p className={`ml-6 group-hover:text-gold-400 transition ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                    18D Cộng Hòa, Phường 4, Tân Bình, TP. Hồ Chí Minh
                                </p>
                            </a>
                            <a href="https://maps.app.goo.gl/w2quBzbuJ89XNVcR9" target="_blank" rel="noopener noreferrer" className="block group">
                                <div className="flex items-center gap-2 mb-1">
                                    <i className={`pi pi-map-marker text-gold-400`}></i>
                                    <span className={`font-semibold group-hover:text-gold-400 transition ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                        Sân Bóng Cầu Xéo (Tân Phú)
                                    </span>
                                </div>
                                <p className={`ml-6 group-hover:text-gold-400 transition ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                    15/12/2 Cầu Xéo, Tân Quý, Tân Phú, TP. Hồ Chí Minh
                                </p>
                            </a>
                            <a href="https://maps.app.goo.gl/qUbqHokqVkEN9MfZ8" target="_blank" rel="noopener noreferrer" className="block group">
                                <div className="flex items-center gap-2 mb-1">
                                    <i className={`pi pi-map-marker text-gold-400`}></i>
                                    <span className={`font-semibold group-hover:text-gold-400 transition ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                        Sân Bóng Gia Nguyễn
                                    </span>
                                </div>
                                <p className={`ml-6 group-hover:text-gold-400 transition ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                    252 Đ. Phan Anh, Phú Thạnh, Hồ Chí Minh
                                </p>
                            </a>
                        </div>
                    </motion.div>
                </div>

                {/* Bottom */}
                <div className={`border-t ${theme === 'dark' ? 'border-white/10' : 'border-gray-300'} mt-8 pt-4 text-center ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'} text-xs`}>
                    © {new Date().getFullYear()} SAIGON FC
                </div>
            </div>
        </footer>
    )
}